import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { ChakraProvider } from "@chakra-ui/react";
import theme from "./theme/theme.ts";
import { PostHogProvider } from "posthog-js/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { registerSW } from "virtual:pwa-register";
import { POSTHOG_KEY } from "./api/base";
import { globalQueryClient } from "./lib/queryClient";
import { initSentry } from "./lib/sentry";
import { reportWebVitals } from "./lib/webVitals";
import { initMetaPixel } from "./lib/metaPixel";
import { initTikTokPixel } from "./lib/tiktokPixel";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { SessionReplayGate } from "./components/SessionReplayGate";
import {
  isRetiredFluentHost,
  retiredRedirectPath,
  RETIRED_REDIRECT_ORIGIN,
} from "./lib/legacyRedirect";

const hostname = typeof window !== "undefined" ? window.location.hostname : "";

if (hostname && isRetiredFluentHost(hostname)) {
  // fluentwithparrot.com is being retired; its users move to the canonical
  // app.parrotapp.com. Compute the destination up front, best-effort tear down
  // the installed PWA's service worker + caches (so it stops serving the old
  // shell), then forward. A short timeout guarantees we redirect even if the
  // cleanup hangs, so a user is never stuck on the dead domain. Gated on
  // hostname (inert on app.parrotapp.com + local dev); the normal app boot in
  // the else branch is skipped, so the SW we're removing isn't re-registered.
  const dest =
    RETIRED_REDIRECT_ORIGIN +
    retiredRedirectPath(window.location.pathname) +
    window.location.search +
    window.location.hash;
  let redirected = false;
  const go = () => {
    if (!redirected) {
      redirected = true;
      window.location.replace(dest);
    }
  };
  void (async () => {
    // Independent best-effort cleanups — a failure in one must not skip the
    // other (or a stale SW / stale Workbox cache is left behind).
    try {
      const regs = (await navigator.serviceWorker?.getRegistrations?.()) ?? [];
      await Promise.allSettled(regs.map((r) => r.unregister()));
    } catch {
      // best effort
    }
    try {
      if (typeof caches !== "undefined") {
        const keys = await caches.keys();
        await Promise.allSettled(keys.map((k) => caches.delete(k)));
      }
    } catch {
      // best effort
    }
    go();
  })();
  setTimeout(go, 1500);
} else {
  // Observability — each is a no-op until its env var / flag is configured.
  initSentry(); // VITE_SENTRY_DSN
  initMetaPixel(); // VITE_META_PIXEL_ID
  initTikTokPixel(); // VITE_TIKTOK_PIXEL_ID

  if (import.meta.env.DEV) {
    // Dev runs with NO service worker (vite.config devOptions.enabled = false).
    // Actively tear down any SW + caches a previous dev build registered, so a
    // stale workbox NetworkFirst worker can't keep intercepting API calls and
    // surfacing "no-response" / stale-backend-URL errors. Self-heals on reload.
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .getRegistrations()
        .then((regs) => regs.forEach((r) => r.unregister()))
        .catch(() => {});
    }
    if (typeof caches !== "undefined") {
      caches.keys().then((keys) => keys.forEach((k) => caches.delete(k))).catch(() => {});
    }
  } else {
    registerSW({ immediate: true });
  }

  const options = {
    api_host: "https://us.i.posthog.com",
    // Recording stays OFF at init so posthog-js never starts a replay in the
    // window between /decide arriving and the `session_replay_enabled` flag
    // resolving. SessionReplayGate starts recording only once the flag is on,
    // mirroring iOS (which never records without the flag).
    disable_session_recording: true,
    autocapture: false,
  };

  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <ErrorBoundary>
        <QueryClientProvider client={globalQueryClient}>
          <PostHogProvider apiKey={POSTHOG_KEY} options={options}>
            <SessionReplayGate />
            <ChakraProvider theme={theme}>
              <App />
            </ChakraProvider>
          </PostHogProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </React.StrictMode>
  );

  // Core Web Vitals → PostHog (the dep was installed but never wired).
  reportWebVitals();
}
